/**
 * M3X.WORKSPACE F.2 + F.12 + F.13 — Rail 2.
 *
 * F.2 — Expanded state with inline-expand (one at a time per §0.2).
 * F.3 — Thin state (44px icon column).
 * F.4 — Hidden state.
 * F.12 — Pinned + Domains sections; pin/unpin via star button on hover.
 * F.13 — Attention dots (Thin) + count chips (Expanded) driven by
 *        `attentionCounts` prop. Aggregator wired by parent
 *        (`useAttentionSignals`).
 *
 * Reads from `navigation/registry.ts`. Sub-domain ordering: pinned
 * (user order) → registration order.
 */

import {
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Star,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { ChevronHintTooltip } from "./ChevronHintTooltip";
import { Rail2Popout } from "./Rail2Popout";
import { useOptionalUserId } from "./useOptionalUserId";

import {
  type ModuleId,
  type SubDomain,
  screenForRoute,
  subDomainsForModule,
} from "../navigation/registry";
import type { AttentionMap } from "./useAttentionSignals";


export type Rail2State = "expanded" | "thin" | "hidden";


export interface Rail2Props {
  /** Which module's sub-domains to render. */
  module: ModuleId;
  /** Rail 2 state. */
  state?: Rail2State;
  /** Current locale ("en" | "ar"). */
  locale?: "en" | "ar";
  /** F.12 — pinned sub-domain ids; rendered in their own section above Domains. */
  pinnedSubDomainIds?: string[];
  /** F.12 — pin/unpin callback. */
  onTogglePin?: (subDomainId: string) => void;
  /** F.13 — attention counts (sub-domain id → count). */
  attentionCounts?: AttentionMap;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2.5 PR 4 — chevron click handler.
   * Cycles Rail 2 state Expanded → Thin → Hidden → Expanded.
   * Wired to useWorkspace.cycleRail2() in WorkspaceShell.
   */
  onCycle?: () => void;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2.7 PR 3 — re-expand to Expanded
   * (specifically — NOT cycle). Used when a Thin-state popout
   * navigates: per closure-hotfix §3.B the user "lands oriented"
   * in Expanded state.
   */
  onExpand?: () => void;
}


export function Rail2({
  module,
  state = "expanded",
  locale = "en",
  pinnedSubDomainIds = [],
  onTogglePin,
  attentionCounts = {},
  onCycle,
  onExpand,
}: Rail2Props) {
  const { t } = useTranslation();
  const userId = useOptionalUserId();
  const location = useLocation();
  const subDomains = useMemo(() => subDomainsForModule(module), [module]);

  const pinned = useMemo(
    () =>
      pinnedSubDomainIds
        .map((id) => subDomains.find((sd) => sd.id === id))
        .filter((sd): sd is SubDomain => !!sd),
    [pinnedSubDomainIds, subDomains],
  );
  const unpinned = useMemo(
    () => subDomains.filter((sd) => !pinnedSubDomainIds.includes(sd.id)),
    [pinnedSubDomainIds, subDomains],
  );

  const activeMatch = screenForRoute(location.pathname);
  const activeSubDomainId = activeMatch?.subDomain.id ?? null;
  // M3X.WORKSPACE-CLOSURE Session 2.9 PR 6 — single source of truth
  // for active screen highlight. screenForRoute uses longest-prefix-wins,
  // so when a sub-domain has overlapping routes (Grid /attendance vs
  // Bulk Adjustments /attendance/bulk-adjustments), exactly ONE wins.
  const activeScreenId = activeMatch?.screen.id ?? null;

  const [expandedId, setExpandedId] = useState<string | null>(
    activeSubDomainId,
  );

  // M3X.WORKSPACE-CLOSURE Session 2.7 PR 3.B — Thin-state hover popout
  // state. 150ms open delay, 300ms close delay per closure-hotfix spec.
  // FINAL PR 4 — popout positions per-icon (insetInlineStart:100% of
  // its wrapper) so popoutTopPx + activePopoutSd are no longer needed.
  const [popoutSubDomainId, setPopoutSubDomainId] = useState<string | null>(
    null,
  );
  const _openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const _closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function _scheduleOpen(sdId: string, _top: number) {
    if (_closeTimerRef.current) clearTimeout(_closeTimerRef.current);
    if (_openTimerRef.current) clearTimeout(_openTimerRef.current);
    _openTimerRef.current = setTimeout(() => {
      setPopoutSubDomainId(sdId);
    }, 150);
  }

  function _scheduleClose() {
    if (_openTimerRef.current) clearTimeout(_openTimerRef.current);
    if (_closeTimerRef.current) clearTimeout(_closeTimerRef.current);
    _closeTimerRef.current = setTimeout(() => {
      setPopoutSubDomainId(null);
    }, 300);
  }

  function _closeImmediately() {
    if (_openTimerRef.current) clearTimeout(_openTimerRef.current);
    if (_closeTimerRef.current) clearTimeout(_closeTimerRef.current);
    setPopoutSubDomainId(null);
  }

  if (state === "hidden") return null;

  if (state === "thin") {
    return (
      <aside
        aria-label="Sub-domains (thin)"
        className="relative flex w-[44px] flex-col items-center border-e border-border-default bg-surface-default"
      >
        {/* M3X.WORKSPACE-CLOSURE Session 2.9 PR 4 — dedicated header
            slot in Thin state (44×36) with just the chevron centered.
            Border-bottom separates it from the icon list below. */}
        {onCycle && (
          <div
            data-rail2-header
            className="flex w-full items-center justify-center border-b border-slate-200"
            style={{ height: "36px", flex: "0 0 36px" }}
          >
            <button
              type="button"
              onClick={onCycle}
              data-rail2-cycle
              aria-label={t(
                "workspace.rail2.collapse.aria",
                "Collapse sub-domains",
              )}
              title="Cycle rail · press [ to cycle"
              className="relative flex h-6 w-6 items-center justify-center rounded-md transition-shadow focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2260C9]"
              style={{ backgroundColor: "#f1f5f9", color: "#475569" }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "#e2e8f0";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 2px 6px rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "#f1f5f9";
                (e.currentTarget as HTMLElement).style.boxShadow = "";
              }}
            >
              <ChevronsRight className="h-3.5 w-3.5 rtl:rotate-180" />
              <ChevronHintTooltip userId={userId} />
            </button>
          </div>
        )}

        <div className="flex flex-col items-center gap-1 py-3">
        {subDomains.map((sd) => {
          const Icon = sd.icon;
          const isActive = sd.id === activeSubDomainId;
          const count = attentionCounts[sd.id] ?? 0;
          const isPopoutOpen = popoutSubDomainId === sd.id;
          return (
            // M3X.WORKSPACE-CLOSURE FINAL PR 4 — each icon is its OWN
            // positioning parent. Popout sits INSIDE this wrapper and
            // uses insetInlineStart:100% → adjacent to the icon, NOT
            // at the rail's edge. Hover state lives on the wrapper so
            // moving cursor icon→popout doesn't fire mouseLeave.
            <div
              key={sd.id}
              data-sub-domain-wrapper={sd.id}
              className="relative"
              onMouseEnter={() => _scheduleOpen(sd.id, 0)}
              onMouseLeave={_scheduleClose}
            >
              <button
                type="button"
                aria-label={locale === "ar" ? sd.labelAr : sd.labelEn}
                data-sub-domain={sd.id}
                onFocus={() => {
                  if (_openTimerRef.current) clearTimeout(_openTimerRef.current);
                  if (_closeTimerRef.current) clearTimeout(_closeTimerRef.current);
                  setPopoutSubDomainId(sd.id);
                }}
                onBlur={_scheduleClose}
                className={[
                  "relative flex h-10 w-10 items-center justify-center rounded-md text-text-tertiary",
                  "hover:bg-surface-subtle hover:text-text-primary",
                  "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2260C9]",
                  isActive ? "bg-brand-gradient text-white" : "",
                ].join(" ")}
              >
                <Icon className="h-4 w-4" />
                {count > 0 && (
                  <span
                    data-attention-dot
                    className="absolute -top-0.5 -end-0.5 h-2 w-2 rounded-full bg-amber-500"
                  />
                )}
              </button>

              {/* Popout INSIDE the icon wrapper. insetInlineStart:100% +
                  marginInlineStart:4px puts it 4px to the start-side
                  of the icon edge. The wrapper's onMouseLeave handles
                  cursor-out from BOTH icon AND popout — no separate
                  bridging needed (the bridge IS the wrapper). */}
              {isPopoutOpen && (
                <Rail2Popout
                  subDomain={sd}
                  locale={locale}
                  currentPath={location.pathname}
                  topPx={0}
                  perIconAdjacent
                  onClose={_closeImmediately}
                  onScreenClick={() => {
                    _closeImmediately();
                    onExpand?.();
                  }}
                />
              )}
            </div>
          );
        })}
        </div>
      </aside>
    );
  }

  // Expanded
  return (
    <aside
      aria-label="Sub-domains"
      className="relative flex w-[256px] flex-shrink-0 flex-col overflow-y-auto border-e border-border-default bg-surface-default"
    >
      {/* M3X.WORKSPACE-CLOSURE Session 2.9 PR 4 — WORKSPACE header row.
          Lives at the top of Rail 2, separated by a bottom border so the
          chevron no longer crowds the first sub-domain row.
          Layout: [WORKSPACE label] [spacer] [`[` kbd] [chevron] */}
      {onCycle && (
        <div
          data-rail2-header
          className="flex items-center justify-between border-b border-slate-200"
          style={{
            height: "36px",
            padding: "8px 12px",
            flex: "0 0 36px",
          }}
        >
          <span
            data-rail2-workspace-label
            style={{
              fontSize: "10px",
              fontWeight: 600,
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t("workspace.rail2.workspaceLabel", "WORKSPACE")}
          </span>
          <div className="flex items-center" style={{ gap: "4px" }}>
            <kbd
              aria-hidden
              style={{
                backgroundColor: "white",
                border: "1px solid #e2e8f0",
                padding: "1px 5px",
                borderRadius: "3px",
                fontSize: "9px",
                fontFamily: "ui-monospace, SFMono-Regular, monospace",
                color: "#94a3b8",
              }}
            >
              [
            </kbd>
            <button
              type="button"
              onClick={onCycle}
              data-rail2-cycle
              aria-label={t(
                "workspace.rail2.collapse.aria",
                "Collapse sub-domains",
              )}
              title="Collapse rail · press [ to cycle"
              className="relative flex h-6 w-6 items-center justify-center rounded-md transition-shadow focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2260C9]"
              style={{ backgroundColor: "#f1f5f9", color: "#475569" }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "#e2e8f0";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 2px 6px rgba(0,0,0,0.08)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "#f1f5f9";
                (e.currentTarget as HTMLElement).style.boxShadow = "";
              }}
            >
              <ChevronsLeft className="h-3.5 w-3.5 rtl:rotate-180" />
              <ChevronHintTooltip userId={userId} />
            </button>
          </div>
        </div>
      )}

      {/* Sub-domains list — below the header row */}
      <div className="flex flex-col gap-1 p-2">

      {pinned.length > 0 && (
        <>
          <h3
            data-section="pinned"
            className="px-2 pt-3 text-[10px] font-bold uppercase tracking-wider text-text-tertiary"
            style={{ letterSpacing: "0.05em" }}
          >
            {t("workspace.rail2.section.pinned", "PINNED")}
          </h3>
          {pinned.map((sd) => (
            <SubDomainRow
              key={`pinned-${sd.id}`}
              subDomain={sd}
              locale={locale}
              isActive={sd.id === activeSubDomainId}
              isExpanded={expandedId === sd.id}
              onToggle={() =>
                setExpandedId((cur) => (cur === sd.id ? null : sd.id))
              }
              activeScreenId={activeScreenId}
              countChip={attentionCounts[sd.id] ?? 0}
              isPinned={true}
              onTogglePin={onTogglePin}
            />
          ))}
          <div className="my-1 border-t border-border-subtle" />
          <h3
            data-section="domains"
            className="px-2 pt-3 text-[10px] font-bold uppercase tracking-wider text-text-tertiary"
            style={{ letterSpacing: "0.05em" }}
          >
            {t("workspace.rail2.section.domains", "DOMAINS")}
          </h3>
        </>
      )}

      {unpinned.map((sd) => (
        <SubDomainRow
          key={sd.id}
          subDomain={sd}
          locale={locale}
          isActive={sd.id === activeSubDomainId}
          isExpanded={expandedId === sd.id}
          onToggle={() =>
            setExpandedId((cur) => (cur === sd.id ? null : sd.id))
          }
          activeScreenId={activeScreenId}
          countChip={attentionCounts[sd.id] ?? 0}
          isPinned={false}
          onTogglePin={onTogglePin}
        />
      ))}
      </div>
    </aside>
  );
}


function SubDomainRow({
  subDomain,
  locale,
  isActive,
  isExpanded,
  onToggle,
  activeScreenId,
  countChip,
  isPinned,
  onTogglePin,
}: {
  subDomain: SubDomain;
  locale: "en" | "ar";
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2.9 PR 6 — single source of truth.
   * Resolved once at the Rail 2 top level via screenForRoute (which
   * uses longest-prefix-wins). SubDomainRow compares THIS id against
   * each screen.id for active state — eliminates the double-highlight
   * defect caused by naive prefix-matching per row.
   */
  activeScreenId: string | null;
  countChip?: number;
  isPinned?: boolean;
  onTogglePin?: (id: string) => void;
}) {
  const Icon = subDomain.icon;
  const label = locale === "ar" ? subDomain.labelAr : subDomain.labelEn;
  const Chevron = isExpanded ? ChevronDown : ChevronRight;
  const count = countChip ?? 0;
  const showPinControl = onTogglePin !== undefined;

  return (
    <div
      data-sub-domain={subDomain.id}
      // Column stack: header row (toggle + pin as siblings — no nested
      // interactives per 8ae3c5f a11y fix) then screen list below when
      // expanded. Outer flex-col prevents the <ul> sitting beside the
      // toggle in a horizontal flex row (regression from 8ae3c5f).
      className="group/sub-domain flex flex-col rounded-md data-[kbd-focus=true]:outline data-[kbd-focus=true]:outline-2 data-[kbd-focus=true]:outline-offset-[-2px]"
      style={{
        // PR 9 — kbd focus ring color (brand blue #2260C9)
        outlineColor: "#2260C9",
      }}
    >
      <div className="flex w-full items-center">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={[
          "flex flex-1 items-center gap-2 text-sm text-start",
          "hover:bg-surface-subtle",
          // S2.9 PR 2 — focus-visible only
          "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2260C9]",
          isActive ? "font-semibold" : "text-text-secondary",
        ].join(" ")}
        style={
          isActive
            ? {
                // M3X.WORKSPACE-CLOSURE Session 2.7 PR 4 — lift the popout's
                // elegant active treatment: subtle blue-cyan gradient with
                // NO heavy border-left. The gradient alone carries the
                // active state.
                background:
                  "linear-gradient(90deg, #eef2ff 0%, #ecfeff 100%)",
                color: "#1e293b",
                padding: "6px 10px",
                borderRadius: "8px",
                margin: "2px 0",
              }
            : {
                padding: "6px 10px",
                borderRadius: "8px",
                margin: "2px 0",
              }
        }
      >
        <Icon
          className="h-4 w-4 flex-shrink-0"
          style={isActive ? { color: "#2260C9" } : undefined}
        />
        <span className="flex-1 truncate">{label}</span>
        {count > 0 && (
          <span
            data-count-chip
            className="inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-semibold leading-none"
            style={{
              backgroundColor:
                count >= 10 || subDomain.attention ? "#fef2f2" : "#eef2ff",
              color:
                count >= 10 || subDomain.attention ? "#dc2626" : "#4338ca",
            }}
          >
            {count}
          </span>
        )}
        <Chevron className="h-3.5 w-3.5 flex-shrink-0 text-text-tertiary" />
      </button>
      {showPinControl && (
        <button
          type="button"
          onClick={() => onTogglePin!(subDomain.id)}
          aria-label={isPinned ? "Unpin" : "Pin"}
          data-pin-toggle
          data-pinned={isPinned ? "true" : "false"}
          className={[
            "rounded p-0.5 transition-opacity duration-150",
            "opacity-0 group-hover/sub-domain:opacity-100 focus-visible:opacity-100",
            isPinned ? "opacity-100" : "",
            "hover:bg-surface-subtle",
          ].join(" ")}
        >
          <Star
            className="h-[14px] w-[14px]"
            fill={isPinned ? "#f59e0b" : "none"}
            stroke={isPinned ? "#f59e0b" : "#94a3b8"}
            strokeWidth={2}
          />
        </button>
      )}
      </div>

      {isExpanded && (
        <ul
          data-sub-domain-screens
          className="mt-1 w-full ps-7"
          style={{ paddingInlineStart: "8px" }}
        >
          {subDomain.screens.map((screen) => {
            const screenLabel =
              locale === "ar" ? screen.labelAr : screen.labelEn;
            // M3X.WORKSPACE-CLOSURE Session 2.9 PR 6 — strict id equality
            // against the screenForRoute-resolved screen id. No more
            // prefix-overlap collisions (Grid `/operations/attendance`
            // no longer matches Bulk Adjustments
            // `/operations/attendance/bulk-adjustments` simultaneously).
            const isScreenActive = activeScreenId === screen.id;
            return (
              <li key={screen.id} style={{ margin: "1px 0" }}>
                <Link
                  to={screen.route}
                  data-screen={screen.id}
                  aria-current={isScreenActive ? "page" : undefined}
                  className="focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#2260C9]"
                  // M3X.WORKSPACE-CLOSURE Session 2.7 PR 4 — match the
                  // popout's elegant active treatment exactly. NO heavy
                  // brand-deep block, NO border-left — the subtle blue-
                  // cyan gradient + brand-blue text carry the active
                  // state. Inactive items get only padding + radius.
                  style={
                    isScreenActive
                      ? {
                          display: "block",
                          padding: "6px 10px",
                          fontSize: "11px",
                          borderRadius: "6px",
                          fontWeight: 600,
                          color: "#2260C9",
                          background:
                            "linear-gradient(90deg, #eef2ff 0%, #ecfeff 100%)",
                          textDecoration: "none",
                        }
                      : {
                          display: "block",
                          padding: "6px 10px",
                          fontSize: "11px",
                          borderRadius: "6px",
                          color: "#334155",
                          textDecoration: "none",
                        }
                  }
                  onMouseEnter={(e) => {
                    if (!isScreenActive) {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "#f1f5f9";
                      (e.currentTarget as HTMLElement).style.color = "#1e293b";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isScreenActive) {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "";
                      (e.currentTarget as HTMLElement).style.color = "#334155";
                    }
                  }}
                >
                  {screenLabel}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


import { NAVIGATION_REGISTRY } from "../navigation/registry";

/** Registered modules that have at least one sub-domain (helper for tests). */
export function modulesWithSubDomains(): ModuleId[] {
  const set = new Set<ModuleId>();
  for (const sd of NAVIGATION_REGISTRY) set.add(sd.module);
  return Array.from(set);
}


export default Rail2;
